<template>
    <li
        class="vue-dnd-kanban-column"
        :class="{
            'vue-dnd-kanban-column-overed': isBodyOvered,
            'vue-dnd-kanban-column-collapsed': !isExpanded,
            [collapsedClass]: !isExpanded
        }"
        ref="droppableRef"
    >
        <!-- Encabezado de columna -->
        <header class="vue-dnd-kanban-column-header" :class="isExpanded ? 'bg-' + column.color : ''">
            <template v-if="isExpanded">
                <button
                    class="expand-toggle-btn"
                    @click="toggleExpand"
                    :aria-expanded="isExpanded"
                    :aria-label="isExpanded ? 'Colapsar columna' : 'Expandir columna'"
                >
                    <q-icon name="expand_less" />
                </button>
                <div class="column-header-content">
                    <div class="col">
                        <h4 class="kanban-column__title">
                            {{ column.nombre }}
                        </h4>
                        <p class="kanban-column__count">
                            {{ itemsInColumn.length }} items
                        </p>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="collapsed-header">
                    <button
                        class="expand-toggle-btn-collapsed"
                        @click="toggleExpand"
                        :aria-expanded="isExpanded"
                        :aria-label="isExpanded ? 'Colapsar columna' : 'Expandir columna'"
                    >
                        <q-icon name="expand_more" />
                    </button>
                    <h4 class="kanban-column__title-vertical">
                        {{ column.nombre }}
                    </h4>
                    <div class="collapsed-badge">
                        {{ itemsInColumn.length }}
                    </div>
                </div>
            </template>
        </header>

        <!-- Lista de items (drop zone) - solo mostrar si está expandida -->
        <ul
            v-if="isExpanded"
            class="vue-dnd-kanban-column-body"
            :class="{ 'vue-dnd-kanban-column-body-overed': isBodyOvered }"
        >
            <template v-if="itemsInColumn.length > 0">
                <KanbanCard
                    v-for="(item, index) in itemsInColumn"
                    :key="item.id"
                    :item="item"
                    :items="itemsInColumn"
                    :item-index="index"
                    :column="column"
                />
            </template>
            <div v-else class="kanban-column__empty">
                <q-icon name="inbox" size="lg" color="grey-5" />
                <p class="text-body2 text-grey-6 q-mt-sm">Sin items</p>
            </div>
        </ul>

        <!-- Footer -->
        <footer class="vue-dnd-kanban-column-footer" />
    </li>
</template>

<script setup>
import { DnDOperations, useDroppable } from '@vue-dnd-kit/core'
import { computed } from 'vue'
import { useKanbanState } from '@/composables/useKanbanState'
import { useKanbanColumnsStore } from '@/stores/useKanbanColumnsStore'
import PresupuestoService from '@/services/PresupuestoService'
import KanbanCard from './KanbanCard.vue'

const props = defineProps({
    column: {
        type: Object,
        required: true,
    },
    columns: {
        type: Array,
        required: true,
    },
    columnIndex: {
        type: Number,
        required: true,
    },
})

const { items, setItemProcessing } = useKanbanState()
const columnsStore = useKanbanColumnsStore()

const isExpanded = computed(() => columnsStore.isColumnExpanded(props.column.id))

const toggleExpand = () => {
    columnsStore.toggleColumn(props.column.id)
}

const collapsedClass = computed(() => {
    if (isExpanded.value) return ''
    return `bg-${props.column.color}`
})

// Items en esta columna
const itemsInColumn = computed(() => {
    return items.value[props.column.id] || []
})

// Droppable para recibir items
const { elementRef: droppableRef, isOvered: isBodyOvered } = useDroppable({
    groups: ['kanban-column-body'],
    data: computed(() => ({
        source: itemsInColumn.value,
        columnId: props.column.id,
    })),
    events: {
        onDrop: (store) => {
            // Usar DnDOperations.applyTransfer para manejar el movimiento automáticamente
            // store.hovered.zone.value funciona incluso en columnas vacías
            if (store.hovered.zone.value) {
                DnDOperations.applyTransfer(store)

                // Después, actualizar el estado del item si fue transferido a otra columna
                const draggingElementsMap = store.draggingElements.value
                if (draggingElementsMap && draggingElementsMap.size > 0) {
                    const draggedElement = draggingElementsMap.values().next().value
                    const draggedData = draggedElement?.data

                    if (draggedData) {
                        const sourceColumnId = draggedData.sourceColumnId
                        const itemId = draggedData.itemId
                        const targetColumnId = props.column.id

                        // Si fue transferido a otra columna, actualizar el estado
                        if (sourceColumnId !== targetColumnId) {
                            const itemInTarget = items.value[targetColumnId]?.find(
                                (item) => item.id === itemId,
                            )
                            if (itemInTarget) {
                                const sourceIndex = draggedData.index

                                itemInTarget.estadoId = targetColumnId

                                // Persistir el cambio de estado en el backend
                                setItemProcessing(itemId, true)
                                PresupuestoService.estadoUpdate(itemId, targetColumnId)
                                    .catch((error) => {
                                        console.error('No se pudo actualizar el estado del presupuesto', error)

                                        // Si falla, volver la card a su columna y posición original
                                        itemInTarget.estadoId = sourceColumnId
                                        const restoredSource = [...(items.value[sourceColumnId] || [])]
                                        restoredSource.splice(sourceIndex, 0, itemInTarget)

                                        items.value = {
                                            ...items.value,
                                            [targetColumnId]: items.value[targetColumnId].filter(
                                                (item) => item.id !== itemId,
                                            ),
                                            [sourceColumnId]: restoredSource,
                                        }
                                    })
                                    .finally(() => {
                                        setItemProcessing(itemId, false)
                                    })
                            }
                        }
                    }
                }
            }
        },
    },
})


</script>

<style lang="scss" scoped>
.vue-dnd-kanban-column {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 0.75rem;
    min-width: 320px;
    width: 320px;
    background-color: #f9f9f9;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease, transform 0.15s ease, opacity 0.15s ease, width 0.3s ease, background-color 0.3s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &-dragging {
        opacity: 0.8;
        transform: rotate(1deg) scale(1.02);
        z-index: 10;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    }

    &-overed {
        shadow: 0 0 0 2px #2196f3;
    }

    &-collapsed {
        min-width: auto;
        width: 60px;
        padding: 0.5rem;
        border-radius: 12px;

        .vue-dnd-kanban-column-body {
            display: none;
        }

        .vue-dnd-kanban-column-footer {
            display: none;
        }

        .vue-dnd-kanban-column-header {
            border-radius: 8px;
            padding: 8px 4px;
        }
    }
}

.vue-dnd-kanban-column-header {
    padding: 12px;
    flex-shrink: 0;
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;

    .column-header-content {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
    }
}

.collapsed-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    width: 100%;
}

.expand-toggle-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
        background-color: rgba(255, 255, 255, 0.3);
    }

    :deep(.q-icon) {
        font-size: 20px;
    }
}

.expand-toggle-btn-collapsed {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
        background-color: rgba(255, 255, 255, 0.3);
    }

    :deep(.q-icon) {
        font-size: 16px;
    }
}

.vue-dnd-kanban-column-body {
    flex: 1;
    list-style: none;
    margin: 0;
    padding: 8px;
    min-height: 150px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    transition: background-color 0.15s ease;

    &-overed {
        background-color: rgba(33, 150, 243, 0.05);
        border-radius: 8px;
    }
}

.vue-dnd-kanban-column-footer {
    flex-shrink: 0;
    padding: 0;
}

.kanban-column__title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    color: white;
}

.kanban-column__title-vertical {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    color: white;
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
    text-align: center;
    word-break: break-word;
    max-height: 100%;
}

.kanban-column__count {
    margin: 0;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
}

.collapsed-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    color: white;
    font-size: 12px;
    font-weight: 700;
    border: 2px solid white;
}

.kanban-column__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 150px;
    text-align: center;
    padding: 16px;
}
</style>
